import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import '../../styles/nano.css';
import '../../styles/product-surface.css';
import Sidebar, { MobileNav } from './Sidebar';
import SessionSettings from './SessionSettings';
import PlanReview from './PlanReview';
import NanoChat from './NanoChat';
import DsaMode from './DsaMode';
import McqMode from './McqMode';
import KnowledgeHub from './KnowledgeHub';
import KnowledgeHubV2 from './KnowledgeHubV2';
import MemoryPage from './MemoryPage';
import FlashcardMode from './FlashcardMode';
import TutorMode from './tutor/TutorMode';
import * as sessionCache from './sessionCache';
import * as sessionStore from './sessionStore';
import NanoLanding from './NanoLanding';
import HomeDashboard from './HomeDashboard';
import PracticePage from './PracticePage';
import CaseStudyMode from './CaseStudyMode';
import PathDetail from './PathDetail';
import InterviewReport from './InterviewReport';
import ProfileSettings, { ProfileForm, profileIncomplete } from './ProfileSettings';
import NavigationPage from './NavigationPage';
import VoiceInterviewInterface from '../VoiceInterviewInterface';
import APIService from '../../services/APIService';
import { useAuth } from '../../context/AuthContext';
import { logVoiceInterview, logRapidfireQuestion, logRapidfireRound, scopeIds } from './memoryLog';
import { MODES, MODE_ORDER } from './modes';
import RapidfireRoom from './RapidfireRoom';
import BlitzRound from './BlitzRound';
import InterviewsPage from './InterviewsPage';
import InterviewHome from './InterviewHome';
import InterviewSetup from './InterviewSetup';
import WrittenInterview from './WrittenInterview';
import RapidFirePractice from './RapidFirePractice';
import SessionRunner from './SessionRunner';
import { loadRun, saveRun, clearRun } from './sessionPlan';
import { useHubTree } from './InterviewChrome';
import { interviewDefaults, loadPrefs } from './settingsPrefs';
import { indexTree } from './paths';
import { DEFAULT_SETUP, addHistory, isAiType, loadHistory, makeEntry, setupScope } from './interviewData';
import ProductAtmosphere from './ProductAtmosphere';
import { DialogProvider, DialogBridge } from './ui/dialogs';
import { IconChevRight } from './icons';

/* Sidebar width preference — 'full' | 'rail' (icons only) | 'hidden'.
   Kept in localStorage so the choice survives a reload. */
const SIDEBAR_KEY = 'nano_sidebar_v1';
const SIDEBAR_MODES = ['full', 'rail', 'hidden'];
const loadSidebar = () => {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY);
    return SIDEBAR_MODES.includes(v) ? v : 'full';
  } catch { return 'full'; }
};
const saveSidebar = (v) => { try { localStorage.setItem(SIDEBAR_KEY, v); } catch { /* private mode */ } };

/* Home's theme preference — 'light' (default) | 'dark' | 'system', kept in
   localStorage and read synchronously so a refresh never flashes the other theme. */
const THEME_KEY = 'pu_theme_v1';
const loadTheme = () => { try { const v = localStorage.getItem(THEME_KEY); return v === 'dark' || v === 'system' ? v : 'light'; } catch { return 'light'; } };
const DARK_MQ = '(prefers-color-scheme: dark)';
const systemDark = () => { try { return window.matchMedia(DARK_MQ).matches; } catch { return false; } };
const saveTheme = (v) => { try { localStorage.setItem(THEME_KEY, v); } catch { /* private mode */ } };

// Persisted sessions may carry retired mode ids (e.g. 'mix') — sanitize.
const safeMode = (m) => (MODES[m] ? m : 'theory');

// The live accent (--mx) per NON-mode screen; mode screens use MODES[mode].accent.
const SCREEN_ACCENTS = {
  home: '#17325c',        // Prepare Up ink navy — Home is the light, editorial surface
  practice: '#17325c',    // Practice shares Home's surface
  case: '#17325c',        // Case studies too
  landing: '#8b5cf6',
  settings: '#818cf8',    // indigo — planning, the agent's core
  plan: '#a78bfa',        // soft violet — "the agent thought"
  hub: '#2dd4bf',         // teal — the living knowledge graph (editor)
  hub2: '#17325c',        // the Knowledge Hub shares the Prepare Up surface
  memory: '#17325c',      // Progress shares the Prepare Up surface
  discover: '#f87171',    // interview red — the discovery library
  nav: '#38bdf8',         // sky — wayfinding
  profile: '#17325c',     // Settings shares the Prepare Up surface
  path: '#8b5cf6',      // path detail — brand violet; the page tints itself via --pc
  report: '#17325c',      // interview feedback shares the Prepare Up surface
  interviews: '#17325c',  // the Interviews workspace (landing, setup, history, written)
  ivsetup: '#17325c',
  ivhistory: '#17325c',
  written: '#17325c',
  rapid: '#17325c',       // AI Rapid Fire — a Practice mode
  run: '#17325c',         // the continuous practice session
};
// Screens on the light Prepare Up surface (Home's system, with its dark-mode toggle).
const PU_SCREENS = ['home', 'practice', 'case', 'rapid', 'run', 'profile', 'hub2', 'memory', 'interviews', 'ivsetup', 'ivhistory', 'written', 'report'];
const accentFor = (screen, mode) => ((screen === 'chat' && (mode === 'flashcards' || mode === 'mcq' || mode === 'theory')) || screen === 'voice' ? '#17325c'   // Flashcards, MCQ, Theory + the interview call share Prepare Up navy
  : (screen === 'chat' || screen === 'rapidfire' || screen === 'blitz' || screen === 'voice')
  ? MODES[safeMode(mode)].accent
  : (SCREEN_ACCENTS[screen] || '#818cf8'));

/*
 * NanoApp — top-level orchestrator for the DataVerse.ai experience.
 * Owns the two screens (Session Settings ↔ Chat), the active session config,
 * and the currently-selected chat mode. AI Interviewer's voice launch is
 * delegated upward via onLaunchVoiceInterview so it can reuse the app's
 * existing WebRTC voice-interview flow.
 */
const DEFAULT_CONFIG = { mode: 'dsa', subjects: [], includedModes: Object.keys(MODES) };

const NanoApp = ({ onLaunchVoiceInterview, onEnterCreator, onEnterAdmin }) => {
  const { user, signOut } = useAuth();
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Learner';
  const displayEmail = user?.email || '';

  const [screen, setScreenState] = useState('home');  // post-login lands on Home; 'home' | 'landing' | 'settings' | 'plan' | 'chat' | 'hub' | 'memory' | 'voice' | 'report'
  const screenRef = useRef('home');
  screenRef.current = screen;
  // Leave guard: a live round (e.g. Blitz) registers `async (target) => boolean`
  // and EVERY screen change — sidebar, hash/back button, mode switch — asks it
  // first. On "stay", the URL is re-asserted so back/forward can't strand the
  // learner on a stale hash.
  const leaveGuardRef = useRef(null);
  const hashRef = useRef(null);
  const setScreen = useCallback((next) => {
    const target = typeof next === 'function' ? next(screenRef.current) : next;
    const guard = leaveGuardRef.current;
    if (guard && target !== screenRef.current) {
      Promise.resolve(guard(target)).then((ok) => {
        if (ok) { leaveGuardRef.current = null; setScreenState(target); }
        else { const h = hashRef.current?.(); if (h && window.location.hash !== h) window.history.replaceState(null, '', h); }
      });
      return;
    }
    setScreenState(target);
  }, []);
  const registerLeaveGuard = useCallback((fn) => { leaveGuardRef.current = fn || null; }, []);

  // Sidebar width. `lastOpen` remembers whether the panel was full or a rail
  // before it was hidden, so bringing it back restores what the learner had.
  const [sidebar, setSidebarState] = useState(loadSidebar);
  const lastOpenRef = useRef(sidebar === 'hidden' ? 'full' : sidebar);
  const setSidebar = useCallback((next) => {
    if (next !== 'hidden') lastOpenRef.current = next;
    setSidebarState(next);
    saveSidebar(next);
  }, []);
  const toggleSidebar = useCallback(() => {
    setSidebar(sidebar === 'full' ? 'rail' : 'full');
  }, [sidebar, setSidebar]);
  const showSidebar = useCallback(() => setSidebar(lastOpenRef.current || 'full'), [setSidebar]);

  // Light / dark. `theming` briefly enables colour transitions so the switch
  // eases (~200ms) without animating every other interaction on the page.
  const [themeMode, setThemeModeState] = useState(loadTheme);   // light | dark | system
  const [sysDark, setSysDark] = useState(systemDark);
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(DARK_MQ); } catch { return undefined; }
    const on = (e) => setSysDark(e.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  const theme = themeMode === 'system' ? (sysDark ? 'dark' : 'light') : themeMode;   // what's shown
  const [theming, setTheming] = useState(false);
  const themingTimer = useRef(null);
  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode); saveTheme(mode);
    setTheming(true);
    clearTimeout(themingTimer.current);
    themingTimer.current = setTimeout(() => setTheming(false), 260);
  }, []);
  // the header toggle flips what's shown (and leaves System for an explicit choice)
  const toggleTheme = useCallback(() => setThemeMode(theme === 'dark' ? 'light' : 'dark'), [theme, setThemeMode]);
  // Settings → Appearance → Reduced motion
  const [reduceMotion, setReduceMotion] = useState(() => loadPrefs().reducedMotion);
  useEffect(() => {
    const on = (e) => setReduceMotion(!!e.detail?.reducedMotion);
    window.addEventListener('pu-prefs', on);
    return () => window.removeEventListener('pu-prefs', on);
  }, []);
  useEffect(() => () => clearTimeout(themingTimer.current), []);
  // Ctrl/Cmd+B — collapse or expand; from hidden it brings the panel back.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'b' && e.key !== 'B') return;
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      // Never steal the key while the learner is typing — Ctrl+B is a cursor
      // binding inside the code editor (a contenteditable) on macOS.
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      e.preventDefault();
      if (sidebar === 'hidden') showSidebar(); else toggleSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebar, toggleSidebar, showSidebar]);
  // The app's own dialogs/toasts, available to handlers defined in this component.
  const uiRef = useRef(null);
  const bindUi = useCallback((api) => { uiRef.current = api; }, []);
  // Continue where the learner left off: restore the persisted session (if any)
  // into the config so Session Settings shows it and every mode has its scope.
  const [saved, setSaved] = useState(() => sessionStore.current());
  const [config, setConfig] = useState(() => (saved
    ? { mode: safeMode(saved.mode || 'theory'), subjects: saved.subjects || [], includedModes: saved.includedModes || Object.keys(MODES), options: saved.options, plan: saved.plan }
    : DEFAULT_CONFIG));
  const [mode, setMode] = useState(() => safeMode((saved && saved.mode) || 'dsa'));

  // Bridge: NanoChat registers its send() here so the sidebar context links
  // (Related / Try-next) can post into the same conversation.
  const chatSendRef = useRef(null);
  const ctx = useMemo(() => MODES[mode].context(config), [mode, config]);

  // MCQ quiz panel (progress + navigator) surfaced from the active quiz card
  // so it can render in the left sidebar.
  const [mcqPanel, setMcqPanel] = useState(null);

  // Pre-warm the engine's question queues whenever the scope changes — the
  // ~2s cold start of the first MCQ then happens while the learner is still
  // reading the screen. Keyed on the joined ids so unrelated config changes
  // (options, plan, mode) never re-fire it.
  const scopeKey = scopeIds(config).join(',');
  useEffect(() => {
    if (scopeKey) APIService.engineWarm({ conceptIds: scopeKey.split(',') });
  }, [scopeKey]);

  // The learner's profile — fetched once; gates onboarding and feeds Home.
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    // A FAILED fetch must never look like an EMPTY profile — cold-started
    // backends time out this call and used to pop the onboarding veil on
    // every login. Retry quietly; on repeated failure leave profile null
    // (unknown), which keeps the veil hidden.
    let alive = true;
    const load = (attempt = 0) => {
      APIService.getProfile()
        .then((prof) => { if (alive) setProfile(prof); })
        .catch(() => { if (alive && attempt < 3) setTimeout(() => load(attempt + 1), 5000 * (attempt + 1)); });
    };
    load();
    return () => { alive = false; };
  }, []);
  // The veil shows ONLY for a genuinely fetched profile with required fields
  // missing — first login fills it once; after that it never reappears.
  const needsOnboarding = profile !== null && profileIncomplete(profile);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => { APIService.adminMe().then((d) => setIsAdmin(!!d.is_admin)).catch(() => {}); }, []);

  // Voice interview (rendered inside the shell, not as a full-page takeover).
  const [voiceSession, setVoiceSession] = useState(null);
  const [voiceReport, setVoiceReport] = useState(null);

  // One-time notice shown above Session Settings (e.g. interview launched
  // without topics). Cleared on dismiss.
  const [settingsNotice, setSettingsNotice] = useState('');

  // Propose → confirm: the agent's plan awaiting the learner's decision.
  // Restored from the store so a tab switch (or reload) never loses a built plan.
  const [pending, setPending] = useState(() => (saved && saved.status === 'planned' && saved.plan
    ? { subjects: saved.subjects, includedModes: saved.includedModes, options: saved.options, plan: saved.plan }
    : null));
  const [materializing, setMaterializing] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const matTokenRef = useRef(0);

  // The mode the agent's plan would enter on (top focus area's first mode).
  const entryModeOf = useCallback((plan, includedModes, subjects) => {
    const top = (plan?.focus_areas || [])[0];
    const entry = top && (top.modes || []).find((m) => m !== 'interviewer' && (includedModes || []).includes(m));
    if (entry) return entry;
    return (subjects || []).some((s) => s.id === 'dsa' || String(s.id).startsWith('dsa.')) ? 'dsa' : 'theory';
  }, []);

  const startSession = useCallback((cfg) => {
    // Seed the pre-generated question cache so every mode opens instantly.
    const conceptIds = (cfg.subjects || []).map((s) => s.id);
    if (cfg.plan && cfg.plan.by_mode) sessionCache.seed(conceptIds, cfg.plan.by_mode);
    setConfig(cfg);
    setMode(cfg.mode);
    setScreen('chat');
    // Persist so the learner can continue this session on their next visit.
    // Reuses the id written when the plan was built, so planned → active is
    // ONE session, not two history entries.
    setSaved(sessionStore.save({
      id: cfg.id, subjects: cfg.subjects, includedModes: cfg.includedModes,
      options: cfg.options, plan: cfg.plan, mode: cfg.mode, status: 'active',
    }));
  }, [setScreen]);

  // Resume the persisted session exactly where the learner left off; a plan
  // that was built but never started reopens on the review screen. If the
  // question cache is cold (new browser session), warm it in the background.
  const resumeSession = useCallback(() => {
    if (!saved) return;
    if (saved.status === 'planned' && saved.plan) {
      setPending({ subjects: saved.subjects, includedModes: saved.includedModes, options: saved.options, plan: saved.plan });
      setScreen('plan');
      return;
    }
    setConfig({ mode: safeMode(saved.mode || 'theory'), subjects: saved.subjects || [], includedModes: saved.includedModes || Object.keys(MODES), options: saved.options, plan: saved.plan });
    setMode(safeMode(saved.mode || 'theory'));
    setScreen('chat');
    const conceptIds = (saved.subjects || []).map((s) => s.id);
    const cold = ['theory', 'mcq', 'flashcards'].every((m) => sessionCache.count(conceptIds, m) === 0);
    if (conceptIds.length && cold) {
      APIService.materializeSession({
        conceptIds, modes: saved.includedModes || [],
        focusAreas: (saved.plan && saved.plan.focus_areas) || [],
        difficulty: saved.options?.difficulty || null,
        codingDifficulty: saved.options?.codingDifficulty || null,
        countPerMode: saved.options?.sessionSize || 5,
      }).then((r) => { if (r && r.by_mode) sessionCache.seed(conceptIds, r.by_mode); })
        .catch(() => { /* modes fall back to live generation */ });
    }
  }, [saved, setScreen]);

  // Open the FULL plan (rationale + progress table) for the current session —
  // works for active sessions too, not just planned ones.
  const viewPlan = useCallback(() => {
    if (!saved || !saved.plan) return;
    setPending({ subjects: saved.subjects, includedModes: saved.includedModes, options: saved.options, plan: saved.plan });
    setScreen('plan');
  }, [saved, setScreen]);

  const newSession = useCallback(() => {
    sessionStore.clearCurrent();
    sessionStore.clearDraft();
    sessionCache.clear();          // a new session must not replay old items
    setSaved(null);
    setPending(null);
    setContentReady(false);
    setConfig(DEFAULT_CONFIG);
    setScreen('settings');
  }, [setScreen]);

  // SessionSettings hands over the agent's proposed plan → review screen.
  // The session is persisted IMMEDIATELY (status 'planned'), so switching tabs
  // or reloading never loses a built plan — it shows as the current session.
  const openPlan = useCallback((p) => {
    setPending(p);
    setContentReady(false);
    setScreen('plan');
    setSaved((prev) => sessionStore.save({
      id: prev && prev.status === 'planned' ? prev.id : undefined,   // re-planning revises, doesn't fork
      subjects: p.subjects, includedModes: p.includedModes,
      options: p.options, plan: p.plan,
      mode: entryModeOf(p.plan, p.includedModes, p.subjects),
      status: 'planned',
    }));
  }, [entryModeOf, setScreen]);

  // Background materialization: while the learner reads the plan, turn its
  // focus areas into ready-to-play content so Start is instant.
  const pendingPlan = pending && pending.plan;
  useEffect(() => {
    if (screen !== 'plan' || !pendingPlan) return;
    const token = ++matTokenRef.current;
    const conceptIds = (pending.subjects || []).map((s) => s.id);
    // Re-viewing the plan of a session whose content is already cached must not
    // re-materialize (LLM cost) — just report ready.
    if (['theory', 'mcq', 'flashcards'].some((m) => sessionCache.count(conceptIds, m) > 0)) {
      setContentReady(true);
      setMaterializing(false);
      return;
    }
    setMaterializing(true);
    setContentReady(false);
    APIService.materializeSession({
      conceptIds,
      modes: pending.includedModes,
      focusAreas: pendingPlan.focus_areas || [],
      difficulty: pending.options?.difficulty || null,
      codingDifficulty: pending.options?.codingDifficulty || null,
      countPerMode: pending.options?.sessionSize || 5,
    }).then((r) => {
      if (matTokenRef.current !== token) return;
      if (r && r.by_mode) {
        sessionCache.seed(conceptIds, r.by_mode);
        setContentReady(true);
      }
    }).catch(() => { /* modes fall back to live generation */ }).finally(() => {
      if (matTokenRef.current === token) setMaterializing(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, pendingPlan]);

  // The human-in-the-loop revision: natural-language feedback → revised plan.
  const adjustPlan = useCallback(async (feedback) => {
    if (!pending) return null;
    const conceptIds = pending.subjects.map((s) => s.id);
    const plan = await APIService.buildPlan({
      conceptIds,
      modes: pending.includedModes,
      focus: pending.options?.focus,
      difficulty: pending.options?.difficulty,
      codingDifficulty: pending.options?.codingDifficulty,
      weakPriority: pending.options?.weakPriority,
      countPerMode: pending.options?.sessionSize || 5,
      feedback,
      previousPlan: pending.plan,
    });
    // The old prepared questions were built for the PRE-revision plan — drop
    // them so the materialize effect regenerates content for the revised one
    // (otherwise "✓ Questions ready" would lie about stale items).
    sessionCache.clearScope(conceptIds);
    setContentReady(false);
    setPending((prev) => (prev ? { ...prev, plan } : prev));
    setSaved((prev) => (prev ? sessionStore.touch({ plan }) : prev));   // revision updates the stored session
    return plan;
  }, [pending]);

  // Confirm: enter the session on the agent's top focus area's first mode.
  const confirmPlan = useCallback(() => {
    if (!pending) return;
    const { subjects, includedModes, options, plan } = pending;
    startSession({
      id: saved?.id,
      mode: entryModeOf(plan, includedModes, subjects),
      subjects, includedModes, options, plan,
    });
  }, [pending, saved, entryModeOf, startSession]);

  // Start the voice interview inside the shell. The graph/session topics may
  // not exist in the backend content, so load an available subject and use ITS
  // real topics (falling back to all).
  const launchVoice = useCallback(async (cfgOverride) => {
    // Resolve the scope AT CALL TIME — this callback can close over a stale
    // config, and an unscoped interview must never launch (it would fall back
    // to off-topic bank questions). No scope anywhere → Session Settings.
    // Interview Setup passes its own scope (the chosen role + focus).
    let cfg = cfgOverride && cfgOverride.subjects && cfgOverride.subjects.length ? cfgOverride : config;
    if (!cfg?.subjects?.length) {
      const s = sessionStore.current();
      if (s && s.subjects && s.subjects.length) {
        cfg = { mode: 'interviewer', subjects: s.subjects, includedModes: s.includedModes || DEFAULT_CONFIG.includedModes, options: s.options, plan: s.plan };
        setConfig(cfg);   // report logging reads scopeIds(config) — keep it true
      } else {
        setSettingsNotice('Pick your topics first — your interview is built from them.');
        setScreen('settings');
        return;
      }
    }
    try {
      const { subjects = [] } = await APIService.getSubjects();
      if (!subjects.length) throw new Error('No study content is available on the server.');
      await APIService.loadSubject(subjects[0]);
      const { topics = [] } = await APIService.getTopics();
      if (!topics.length) throw new Error('The loaded subject has no topics.');
      const wanted = (cfg?.subjects || []).map((s) => String(s.name).toLowerCase());
      const matched = topics.filter((t) => wanted.includes(String(t).toLowerCase()));
      const useTopics = matched.length ? matched : topics;
      const session = await APIService.startSession(useTopics, null, true, {
        voice_interview: true,
        // the AI Selector builds the interview from the learner's memory —
        // unless Interview Setup turned personalization off
        scope_ids: scopeIds(cfg),
        learner_id: cfg.personalize === false ? undefined : (user?.uid || undefined),
      });
      if (!session || !session.session_id) throw new Error(session?.error || 'Could not create a session.');
      setVoiceSession({ ...session, voice_interview: true });
      setScreen('voice');
    } catch (err) {
      uiRef.current?.toast.error(`Could not start the voice interview: ${err.message}`);
      setScreen('home');   // never strand the learner on an eternal spinner
    }
  }, [config, user, setScreen]);


  // Sidebar mode click: enter chat in that mode. If the in-memory config has
  // no topics yet, fall back to the persisted session's scope — the last
  // session's topics are always configured, so modes never dead-end on
  // "No topics configured" while a session exists.
  const selectMode = useCallback((id) => {
    setMode(id);
    if (id === 'interviewer') {
      // No chat detour — open the interview room immediately; the session is
      // created while the "preparing" state shows.
      setIvOrigin({ kind: 'voice', setup: null, title: 'AI Interview' });
      setVoiceSession(null);
      setScreen('voice');
      launchVoice();
      setSaved((prev) => (prev ? sessionStore.touch({ mode: id }) : prev));
      return;
    }
    if (id === 'rapidfire' || id === 'blitz') {
      // Blitz rounds — no chat page; the room self-launches from config.
      // 'rapidfire' is the spoken AI round, 'blitz' the typed round.
      setConfig((prev) => {
        if (prev && prev.subjects && prev.subjects.length) return prev;
        const s = sessionStore.current();
        if (s && s.subjects && s.subjects.length) {
          return { mode: id, subjects: s.subjects, includedModes: s.includedModes || DEFAULT_CONFIG.includedModes, options: s.options, plan: s.plan };
        }
        return DEFAULT_CONFIG;
      });
      setScreen(id === 'blitz' ? 'blitz' : 'rapidfire');
      setSaved((prev) => (prev ? sessionStore.touch({ mode: id }) : prev));
      return;
    }
    setConfig((prev) => {
      if (prev && prev.subjects && prev.subjects.length) {
        return { ...prev, includedModes: prev.includedModes || DEFAULT_CONFIG.includedModes };
      }
      const s = sessionStore.current();
      if (s && s.subjects && s.subjects.length) {
        return { mode: id, subjects: s.subjects, includedModes: s.includedModes || DEFAULT_CONFIG.includedModes, options: s.options, plan: s.plan };
      }
      return DEFAULT_CONFIG;
    });
    setScreen('chat');
    setSaved((prev) => (prev ? sessionStore.touch({ mode: id }) : prev));
  }, [launchVoice, setScreen]);

  const goHome = useCallback(() => setScreen('settings'), [setScreen]);
  const goHomeScreen = useCallback(() => setScreen('home'), [setScreen]);

  // Zero-setup sample question: drop straight into the tutor on a default
  // scope — value before configuration for brand-new users.
  const startSample = useCallback(() => {
    setConfig({ mode: 'theory', subjects: [{ id: 'ml', name: 'Machine Learning', kind: 'topic' }],
      includedModes: Object.keys(MODES), options: { sessionSize: 3 }, plan: null });
    setMode('theory');
    setScreen('chat');
  }, [setScreen]);
  const [hubNode, setHubNode] = useState(null);   // open concept page in Hub V2
  const openHub = useCallback(() => { setHubNode(null); setScreen('hub2'); }, [setScreen]);
  const openHubNode = useCallback((id) => { setHubNode(id || null); setScreen('hub2'); }, [setScreen]);
  const openInterviews = useCallback(() => setScreen('interviews'), [setScreen]);
  // ── Interviews workspace: setup → room / written → feedback → history ──
  const ivHub = useHubTree();
  const [ivSetup, setIvSetup] = useState(DEFAULT_SETUP);
  const [ivSetupKey, setIvSetupKey] = useState(0);    // remounts the setup screen for each new prefill
  const [ivOrigin, setIvOrigin] = useState(null);     // what the current room was launched from
  const [ivMeta, setIvMeta] = useState(null);         // the history entry the report shows
  const [ivHistory, setIvHistory] = useState(() => loadHistory(user?.uid));
  useEffect(() => { setIvHistory(loadHistory(user?.uid)); }, [user]);
  const openIvSetup = useCallback((prefill = {}) => {
    // only explicit choices go in — the setup fills the rest from the learner's goal and defaults
    setIvSetup({ ...interviewDefaults(), ...prefill });   // Settings → Practice & Interviews
    setIvSetupKey((k) => k + 1);
    setScreen('ivsetup');
  }, [setScreen]);
  const recordInterview = useCallback((kind, report, setupUsed, title) => {
    const entry = makeEntry({ kind, setup: setupUsed, report, title });
    setIvHistory(addHistory(user?.uid, entry));
    setIvMeta(entry);
    return entry;
  }, [user]);
  const startInterview = useCallback((setupChosen) => {
    setIvSetup(setupChosen);
    if (!isAiType(setupChosen.type)) { setScreen('written'); return; }
    const byId = indexTree(ivHub || []);
    const subjects = setupScope(setupChosen, ivHub ? byId : null)
      .map((id) => ({ id, name: byId[id]?.name || id, kind: 'topic' }));
    const cfg = { mode: 'interviewer', subjects, includedModes: DEFAULT_CONFIG.includedModes, options: {}, plan: null,
      personalize: setupChosen.personalize !== false };
    setConfig(cfg);
    setMode('interviewer');
    setIvOrigin({ kind: setupChosen.type, setup: setupChosen });
    setVoiceSession(null);
    setScreen('voice');
    launchVoice(cfg);
  }, [ivHub, launchVoice, setScreen]);
  const openReport = useCallback((entry) => { setVoiceReport(entry.report); setIvMeta(entry); setScreen('report'); }, [setScreen]);
  const openMemory = useCallback(() => setScreen('memory'), [setScreen]);
  const [viewPath, setViewPath] = useState(null);
  const openPath = useCallback((id) => { setViewPath(id); setScreen('path'); }, [setScreen]);
  const openNav = useCallback(() => setScreen('nav'), [setScreen]);
  const openPractice = useCallback(() => setScreen('practice'), [setScreen]);
  const [rapidSubjects, setRapidSubjects] = useState(null);   // AI Rapid Fire's prefilled topics
  // Continuous practice sessions: the builder makes a run, the runner plays it.
  const [sessionRun, setSessionRun] = useState(() => loadRun());
  const [practiceSeed, setPracticeSeed] = useState(null);   // builder prefill handed back from a finished session
  const openBuilder = useCallback((seed = null) => { setSessionRun(loadRun()); setPracticeSeed(seed); setScreen('practice'); }, [setScreen]);
  // One practice type → that type's own focused screen (MCQ, Flashcards,
  // Theory, Case Study, Rapid Fire). Two or more → the conversational mixed session.
  const createRun = useCallback((cfg) => {
    if (cfg.types.length === 1) {
      const t = cfg.types[0];
      const byId = indexTree(ivHub || []);
      const subjects = cfg.scopeIds.map((id) => ({ id, name: byId[id]?.name || id, kind: 'topic' }));
      setPracticeSeed(null);
      startPracticeRef.current?.({ mode: t, subjects, size: cfg.counts[t] || 5 });
      return;
    }
    const run = { cfg, idx: 0, results: [], startedAt: Date.now() };
    saveRun(run); setSessionRun(run); setPracticeSeed(null); setScreen('run');
  }, [setScreen, ivHub]);
  const resumeRun = useCallback(() => { const r = loadRun(); if (r) { setSessionRun(r); setScreen('run'); } }, [setScreen]);
  const discardRun = useCallback(() => { clearRun(); setSessionRun(null); }, []);
  // Practice page → straight into a mode on the chosen topics (same shape the
  // Knowledge Hub's "Practice" uses). Blitz is its own room, not a chat.
  const startPracticeRef = useRef(null);
  const startPractice = useCallback(({ mode: m, subjects, size = 5, difficulty = null }) => {
    if (!subjects || !subjects.length) return;
    if (m === 'rapid') {
      // AI Rapid Fire is its own Practice screen: a light setup, prefilled with these topics.
      setRapidSubjects(subjects);
      setScreen('rapid');
      return;
    }
    if (m === 'case') {
      // Case studies are their own screen (an AI-coached scenario), not a chat mode.
      setConfig({ mode: 'theory', subjects, includedModes: Object.keys(MODES), options: {}, plan: null });
      setScreen('case');
      return;
    }
    if (!MODES[m]) return;
    setConfig({ mode: m, subjects, includedModes: Object.keys(MODES), options: { sessionSize: size, ...(difficulty ? { difficulty } : {}) }, plan: null });
    setMode(m);
    setScreen(m === 'blitz' ? 'blitz' : 'chat');
  }, [setScreen]);
  startPracticeRef.current = startPractice;

  /* ── URL routing ──────────────────────────────────────────────────────────
   * Every screen and mode owns a #/ path (#/theory, #/course, #/memory…), so a
   * refresh, a deep link, or back/forward restores the page instead of dumping
   * the learner on Home. State is the source of truth; the hash mirrors it. */
  const hashForState = useCallback(() => {
    if (screen === 'chat') return `#/${mode}`;
    if (screen === 'voice') return '#/interviewer';
    if (screen === 'nav') return '#/problems';
    if (screen === 'discover') return '#/interviews/library';
    if (screen === 'interviews') return '#/interviews';
    if (screen === 'rapid') return '#/practice/rapid-fire';
    if (screen === 'run') return '#/practice/session';
    if (screen === 'ivsetup') return '#/interviews/setup';
    if (screen === 'ivhistory') return '#/interviews/history';
    if (screen === 'written') return '#/interviews/written';
    if (screen === 'hub2') return hubNode ? `#/hub2/${hubNode}` : '#/hub2';
    if (screen === 'path' && viewPath) return `#/path/${viewPath}`;
    if (['home', 'practice', 'case', 'settings', 'plan', 'hub', 'memory', 'profile',
         'rapidfire', 'blitz'].includes(screen)) return `#/${screen}`;
    return null;   // transient screens (report, landing) keep the URL
  }, [screen, mode, viewPath, hubNode]);

  hashRef.current = hashForState;

  const applyHash = useCallback(() => {
    const target = window.location.hash.replace(/^#\/?/, '') || 'home';
    const current = (hashForState() || '').replace(/^#\//, '');
    if (target === current) return;            // our own write echoing back — no-op
    const [head, arg] = target.split('/');
    if (MODE_ORDER.includes(head)) { selectMode(head); return; }
    switch (head) {
      case 'practice':
        if (arg === 'session') { const r = loadRun(); setSessionRun(r); setScreen(r ? 'run' : 'practice'); break; }
        setScreen(arg === 'rapid-fire' ? 'rapid' : 'practice'); break;   // #/practice/new is the Practice page itself
      case 'case': setScreen('case'); break;
      case 'settings': setScreen('settings'); break;
      case 'plan': setScreen(pending ? 'plan' : 'home'); break;
      case 'problems': case 'course': setScreen('nav'); break;
      case 'interviews':
        // a written round lives in memory only — a refresh lands back on setup
        setScreen(arg === 'library' ? 'discover' : arg === 'history' ? 'ivhistory' : arg === 'setup' || arg === 'written' ? 'ivsetup' : 'interviews');
        break;
      case 'hub': setScreen('hub'); break;
      case 'hub2': setHubNode(arg || null); setScreen('hub2'); break;
      case 'memory': setScreen('memory'); break;
      case 'community': setScreen('interviews'); break;   // Community is paused — old links land on Interviews
      case 'profile': setScreen('profile'); break;
      case 'path': if (arg) openPath(arg); else setScreen('home'); break;
      default: setScreen('home');
    }
  }, [hashForState, selectMode, openPath, pending, setScreen]);

  const bootedRef = useRef(false);
  useEffect(() => {                           // restore once on mount (refresh / deep link)
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (window.location.hash.replace(/^#\/?/, '')) applyHash();
  }, [applyHash]);
  useEffect(() => {                           // back/forward + manual URL edits
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [applyHash]);
  useEffect(() => {                           // state → URL
    const h = hashForState();
    if (h && window.location.hash !== h) window.location.hash = h;
  }, [hashForState]);
  const registerSend = useCallback((fn) => { chatSendRef.current = fn; }, []);
  const askInChat = useCallback((t) => chatSendRef.current?.(t), []);
  const registerMcq = useCallback((p) => setMcqPanel(p), []);

  // The interviewer has no chat page — any route that lands there opens the
  // interview room instead (resume, restored sessions, stray links).
  useEffect(() => {
    if (screen === 'chat' && mode === 'interviewer') {
      setVoiceSession(null);
      setScreen('voice');
      launchVoice();
    }
  }, [screen, mode, launchVoice, setScreen]);

  useEffect(() => {
    if (screen === 'chat' && mode === 'rapidfire') setScreen('rapidfire');
    if (screen === 'chat' && mode === 'blitz') setScreen('blitz');
  }, [screen, mode, setScreen]);

  const onVoiceComplete = useCallback((report) => {
    logVoiceInterview(report, scopeIds(config));
    recordInterview(ivOrigin?.kind || 'voice', report, ivOrigin?.setup || null, ivOrigin?.title);
    setVoiceReport(report);
    setScreen('report');
  }, [config, ivOrigin, recordInterview, setScreen]);
  const onWrittenComplete = useCallback((report) => {
    recordInterview('written', report, ivSetup);
    setVoiceReport(report);
    setScreen('report');
  }, [ivSetup, recordInterview, setScreen]);
  const onRapidfireComplete = useCallback((report) => {
    (report.question_evaluations || []).forEach((q) => {
      if (q.concept_ids && q.concept_ids.length) logRapidfireQuestion(q.concept_ids, q.status || (q.score >= 60 ? 'correct' : 'wrong'));
    });
    logRapidfireRound(report, scopeIds(config));
    recordInterview('rapidfire', report, null, 'Rapid-fire round');
    setVoiceReport(report);
    setScreen('report');
  }, [config, recordInterview, setScreen]);
  const exitVoice = useCallback(() => { setVoiceSession(null); setScreen('interviews'); }, [setScreen]);

  return (
    <div className={`nano-root${screen === 'landing' ? ' is-landing' : ''}${PU_SCREENS.includes(screen) || (screen === 'chat' && (mode === 'flashcards' || mode === 'mcq' || mode === 'theory')) ? ` is-light${theme === 'dark' ? ' pu-dark' : ''}` : ''}${theming ? ' pu-theming' : ''}${reduceMotion ? ' pu-reduce-motion' : ''}`} style={{ '--mx': accentFor(screen, mode) }}>
      <DialogProvider>
      <DialogBridge bind={bindUi} />
      {screen === 'landing' ? (
        <NanoLanding onStart={goHome} onSelectMode={selectMode} onOpenHub={openHub} />
      ) : (
        <div className="nano-shell">
          <div className="nano-glow" aria-hidden="true" />
          {sidebar === 'hidden' && (
            <button className="nano-side-handle" type="button" onClick={showSidebar}
              title="Show sidebar (Ctrl+B)" aria-label="Show sidebar" aria-controls="nano-sidebar">
              <IconChevRight size={12} />
            </button>
          )}
          {sidebar !== 'hidden' && (
          <Sidebar
            screen={screen}
            activeMode={screen === 'chat' ? mode : null}
            onSelectMode={selectMode}
            onHome={goHome}
            onOpenHome={goHomeScreen}
            onBrand={goHomeScreen}
            onOpenHub={openHub}
            onOpenMemory={openMemory}
            onOpenProfile={() => setScreen('profile')}
            onOpenInterviews={openInterviews}
            onOpenPractice={openPractice}
            avatar={profile?.avatar || ''}
            onEnterCreator={onEnterCreator}
            onEnterAdmin={isAdmin ? onEnterAdmin : undefined}
            onOpenNav={openNav}
            context={screen === 'chat' ? ctx : null}
            accent={MODES[mode].accent}
            onAsk={askInChat}
            mcqPanel={screen === 'chat' && mode === 'mcq' ? mcqPanel : null}
            userName={displayName}
            userEmail={displayEmail}
            onSignOut={signOut}
            collapsed={sidebar === 'rail'}
            onToggleCollapse={toggleSidebar}
            onHide={() => setSidebar('hidden')}
          />
          )}
          <div className={`nano-main nano-main--${screen} nano-main--mode-${mode}`} data-screen={screen} data-mode={mode}>
            <ProductAtmosphere screen={screen} mode={mode} />
            <div className="nano-main-content">
            {screen === 'settings' && settingsNotice && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', margin: '0 0 12px', background: 'var(--n-primary-soft)', border: '1px solid var(--n-primary-line)', borderRadius: 12, fontSize: 13.5 }}>
                <span style={{ color: 'var(--n-primary-ink)', fontWeight: 600, flex: 1 }}>{settingsNotice}</span>
                <button onClick={() => setSettingsNotice('')} style={{ border: 'none', background: 'none', color: 'var(--n-text-3)', cursor: 'pointer', fontSize: 13 }}>Dismiss</button>
              </div>
            )}
            {needsOnboarding && screen !== 'profile' && (
              <div className="pf-onboard-veil" role="dialog" aria-modal="true" aria-label="Complete your profile">
                <div className="pf-onboard">
                  <h2>Welcome — set up your profile</h2>
                  <p>Display name, birthday, and LinkedIn are required to continue. You can edit everything later in Settings.</p>
                  <ProfileForm profile={profile} compact onSaved={setProfile} />
                </div>
              </div>
            )}
            {screen === 'profile' ? (
              <ProfileSettings profile={profile} onProfileSaved={setProfile}
                theme={theme} themeMode={themeMode} onSetTheme={setThemeMode} onToggleTheme={toggleTheme}
                onOpenHubNode={openHubNode} onOpenPath={openPath} />
            ) : screen === 'home' ? (
              <HomeDashboard
                profile={profile}
                onEditProfile={() => setScreen('profile')}
                session={saved}
                onResume={resumeSession}
                onOpenSettings={goHome}
                onOpenPath={openPath}
                onViewPlan={viewPlan}
                onOpenMemory={openMemory}
                onOpenHub={openHub}
                onSample={startSample}
                onOpenDsa={openNav}
                onOpenInterviews={openInterviews}
                onOpenHubNode={openHubNode}
                theme={theme}
                onToggleTheme={toggleTheme}
              />
            ) : screen === 'case' ? (
              <CaseStudyMode config={config} onConfigure={openPractice}
                onBack={openPractice} profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath} onOpenHubNode={openHubNode} />
            ) : screen === 'practice' ? (
              <PracticePage
                profile={profile}
                onEditProfile={() => setScreen('profile')}
                theme={theme}
                onToggleTheme={toggleTheme}
                key={practiceSeed ? JSON.stringify(practiceSeed) : 'practice'}
                seed={practiceSeed}
                resumable={(() => { const r = loadRun(); return r && r.idx < r.cfg.steps.length ? r : null; })()}
                onResumeSession={resumeRun}
                onDiscardSession={discardRun}
                onCreateSession={createRun}
                onOpenPath={openPath}
                onOpenHubNode={openHubNode}
                onOpenCoding={openNav}
              />
            ) : screen === 'settings' ? (
              <SessionSettings
                onPlanReady={openPlan}
                savedSession={saved}
                onResume={resumeSession}
                onViewPlan={viewPlan}
                onNewSession={newSession}
              />
            ) : screen === 'plan' ? (
              <PlanReview
                plan={pending?.plan}
                subjects={pending?.subjects || []}
                isActive={saved?.status === 'active'}
                materializing={materializing}
                contentReady={contentReady}
                onStart={confirmPlan}
                onAdjust={adjustPlan}
                onBack={goHome}
              />
            ) : screen === 'hub2' ? (
              <KnowledgeHubV2
                openNodeId={hubNode}
                onOpenNode={setHubNode}
                onOpenDsa={openNav}
                onStartPractice={startPractice}
                profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath}
                onPractice={(subjects) => {
                  setConfig({ mode: 'theory', subjects, includedModes: Object.keys(MODES), options: { sessionSize: 5 }, plan: null });
                  setMode('theory');
                  setScreen('chat');
                }} />
            ) : screen === 'hub' ? (
              <KnowledgeHub />
            ) : screen === 'memory' ? (
              <MemoryPage onPractice={goHome} onStartPractice={startPractice}
                onOpenHubNode={openHubNode} onOpenHub={openHub} onOpenPath={openPath}
                onOpenInterviews={openInterviews} onStartInterview={() => openIvSetup({ type: 'video' })}
                profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} />
            ) : screen === 'path' && viewPath ? (
              <PathDetail pathId={viewPath} session={saved} onBack={goHomeScreen}
                onOpenSettings={goHome} onResume={resumeSession} onViewPlan={viewPlan} />
            ) : screen === 'discover' ? (
              <InterviewsPage config={config}
                onLaunch={(sess) => {
                  setIvOrigin({ kind: 'library', setup: null, title: sess.title || 'Library interview' });
                  setVoiceSession({ ...sess, voice_interview: true });
                  setScreen('voice');
                }} />
            ) : screen === 'nav' ? (
              <div style={{ height: '100%', overflowY: 'auto', padding: '20px 24px' }}>
                <NavigationPage config={config} registerSend={registerSend} />
              </div>
            ) : screen === 'interviews' || screen === 'ivhistory' ? (
              <InterviewHome view={screen === 'ivhistory' ? 'history' : 'home'} history={ivHistory} hub={ivHub}
                onSetup={openIvSetup} onOpenReport={openReport} onOpenHistory={() => setScreen('ivhistory')}
                onBack={openInterviews} onOpenLibrary={() => setScreen('discover')} onOpenPractice={openPractice}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme}
                onOpenHubNode={openHubNode} onOpenPath={openPath} />
            ) : screen === 'run' && sessionRun ? (
              <SessionRunner key={sessionRun.startedAt} run={sessionRun} hub={ivHub}
                onExit={() => openBuilder(null)} onNewSession={openBuilder} onOpenHubNode={openHubNode}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath} />
            ) : screen === 'rapid' ? (
              <RapidFirePractice key={(rapidSubjects || []).map((x) => x.id).join(',') || 'weak'} initialSubjects={rapidSubjects}
                onBack={openPractice} onStartPractice={startPractice}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme}
                onOpenHubNode={openHubNode} onOpenPath={openPath} />
            ) : screen === 'ivsetup' ? (
              <InterviewSetup key={ivSetupKey} initial={ivSetup} hub={ivHub} history={ivHistory}
                onCancel={openInterviews} onStart={startInterview}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme}
                onOpenHubNode={openHubNode} onOpenPath={openPath} />
            ) : screen === 'written' ? (
              <WrittenInterview setup={ivSetup} hub={ivHub} onExit={openInterviews} onComplete={onWrittenComplete}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme}
                onOpenHubNode={openHubNode} onOpenPath={openPath} />
            ) : screen === 'voice' ? (
              <VoiceInterviewInterface sessionData={voiceSession}
                setup={ivOrigin?.setup} onComplete={onVoiceComplete} onCancel={exitVoice} />
            ) : screen === 'rapidfire' ? (
              <RapidfireRoom config={config} onComplete={onRapidfireComplete} onCancel={() => setScreen('home')} />
            ) : screen === 'blitz' ? (
              <BlitzRound config={config} onConfigure={goHome} onExit={goHomeScreen} registerLeaveGuard={registerLeaveGuard} />
            ) : screen === 'report' ? (
              <InterviewReport
                report={voiceReport}
                meta={ivMeta}
                hub={ivHub}
                onPracticeWeak={goHome}
                onNewInterview={() => (ivMeta?.setup ? openIvSetup(ivMeta.setup)
                  : selectMode(voiceReport?.rapidfire ? 'rapidfire' : 'interviewer'))}
                onRetry={(setupNext) => openIvSetup(setupNext)}
                onDone={openInterviews}
                onStartPractice={startPractice}
                onOpenHubNode={openHubNode}
                profile={profile} onEditProfile={() => setScreen('profile')} theme={theme} onToggleTheme={toggleTheme}
                onOpenPath={openPath}
              />
            ) : mode === 'dsa' ? (
              <DsaMode config={config} registerSend={registerSend} onOpenNav={openNav} onConfigure={goHome} />
            ) : mode === 'mcq' ? (
              <McqMode config={config} onConfigure={goHome}
                onBack={openPractice} profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath} onOpenHubNode={openHubNode} />
            ) : mode === 'flashcards' ? (
              <FlashcardMode config={config} onConfigure={goHome}
                onBack={openPractice} profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath} onOpenHubNode={openHubNode} />
            ) : mode === 'theory' ? (
              <TutorMode config={config} onConfigure={goHome}
                onBack={openPractice} profile={profile} onEditProfile={() => setScreen('profile')}
                theme={theme} onToggleTheme={toggleTheme} onOpenPath={openPath} onOpenHubNode={openHubNode} />
            ) : (
              <NanoChat
                key={mode}
                mode={mode}
                config={config}
                onLaunchVoice={launchVoice}
                registerSend={registerSend}
                registerMcq={registerMcq}
              />
            )}
            </div>
          </div>
          <MobileNav
            screen={screen}
            mode={mode}
            onOpenHome={goHomeScreen}
            onOpenPractice={openPractice}
            onOpenInterviews={openInterviews}
            onOpenHub={openHub}
            onOpenProgress={openMemory}
          />
        </div>
      )}
      </DialogProvider>
    </div>
  );
};

export default NanoApp;
